// Desktop v3 room (D_Desk.dc.html, design record 电脑端-v3-2026-11-03): copy, positions and look values that exist only
// in that prototype. Lines the phone also shows come from shared/src/content (LINES, STUCK_REASONS, CRISIS, AI_NOTE).
import type { CharId } from '@shared/types';

export interface RoomDef {
  /** Card label word: "CUE 2 · 11:30 AM" (D_Desk C[ch].unit). */
  unit: string;
  /** Top-left line: "The theater · Tuesday, 10:26 AM" (D_Desk C[ch].where). */
  where: string;
  /** Ink on her paper (labels, stamps) and the journal (D_Desk C[ch].ink). */
  ink: string;
  /** Where her paper card lies in the picture: center x and bottom y in room coordinates (1440x810 picture). */
  cx: number; by: number;
  /** The top of her head in the picture (y, room coordinates; her loop's highest point, less a margin): a window wider
   *  than the picture crops its top, and never above this. */
  head: number;
  /** What the room picture shows (alt text). */
  alt: string;
  /** Spread foot (D_Desk MOTTO). */
  motto: string;
  /** Her sound in the sound popover is "{poss} place". */
  hum: string;
}

export const ROOM: Record<CharId, RoomDef> = {
  sunny: { unit: 'BIT', where: 'The comedy club', ink: '#8A6408', cx: 708, by: 792, head: 60, alt: 'Sunny writing jokes at the bar of the empty comedy club', motto: "One bit at a time. That's the whole act.", hum: 'Street outside' },
  margo: { unit: 'CUE', where: 'The theater', ink: '#9A5B12', cx: 744, by: 776, head: 30, alt: 'Margo writing cue notes at the tech table in the empty theater', motto: 'One cue at a time.', hum: 'Empty-theater hum' },
  arjun: { unit: 'SQUARE', where: 'The studio', ink: '#3E4F96', cx: 742, by: 790, head: 49, alt: 'Arjun cleaning one small square of the mural in his studio', motto: 'One small square at a time.', hum: 'Quiet studio radio' },
  tavo: { unit: 'INNING', where: 'The press box', ink: '#A44A12', cx: 684, by: 792, head: 59, alt: 'Tavo keeping score in the press box over the ballpark', motto: 'One pitch at a time!', hum: 'Crowd murmur' },
};

/** The room picture's design size (1536x864 stills and 1080p clips, drawn at 1440x810 in D_Desk). */
export const PIC = { w: 1440, h: 810 };
/** The window content's design size (D_Desk's room section, below the macOS menu bar). */
export const STAGE = { w: 1440, h: 792 };

/** Desk card text (D_Desk `dcard`). {n} name, {k} a number, {d}/{t} done/total, {m} minutes. */
export const DESK = {
  blankLabel: 'TODAY · {day}', blankTitle: 'Nothing on the card yet', blankFoot: 'Tell {n} what’s on today',
  draftLabel: 'DRAFT · {k} CARDS', draftFoot: 'From {n} · click to see them all',
  nextFoot: '{m} min · {d} of {t} done',
  sessLabel: 'WORKING TOGETHER', sessFoot: '{m} min planned · with {n}', sessFootBare: 'with {n}',
  stuckLabel: 'STUCK ON · {time}', whenLabel: 'WHEN IS BETTER? · {time}',
  helpFoot: 'From {n} · just this, nothing else',
  doneLabel: 'TODAY · {d} OF {t}', doneTitle: 'Every card is done', doneFoot: 'Day {k} is lit',
  stop: 'Stop working together',
};

/** "It's too big" when the server split the card (phone S: "Use these 3 cards"). */
export const SPLIT_HELP = { head: 'SMALLER CARDS · {k}', btn: 'Use these {k} cards', foot: 'Then: {rest}' };

export const SPREAD = {
  title: 'Today', draftTitle: 'Here’s today',
  sub: '{date} · {d} of {t} done', draftSub: '{date} · {k} cards from {n}',
  draftFoot: 'Heard something wrong? Click a card to fix it.',
  movedFoot: 'Moved to tomorrow: {list}. {motto}',
  down: 'PUT THEM DOWN', downAria: 'Put the cards down',
  upNext: ' · up next', doneAt: 'Done at {time}',
};

export const EDIT = { head: 'CHANGE THIS CARD', what: 'What the card says', when: 'WHEN', long: 'HOW LONG', save: 'Save', remove: 'Remove', takeOff: 'Take it off', mins: [5, 10, 15, 25, 45] };
/** "When should we plan?" on paper (Me row / evening card "Change"): same wheel as the phone sheet. */
export const PLANTIME = { head: 'WHEN SHOULD WE PLAN?', sub: '{n} asks what’s on at this time every day.', done: 'Done' };

export const TITLE = { more: 'CLICK FOR MORE', back: 'CLICK TO GET BACK TO IT', perfectPts: ' +30 points.', levelNew: ' New: {x}.', nextSeason: 'something new next season' };

export const TOP = {
  buddyAria: '{n}, your buddy', sound: 'Sound', only: 'Only {n}: hide everything else', showAll: 'SHOW EVERYTHING',
  streakAria: '{k}-day streak', streakLit: ', lit today',
};

export const TALK = { placeholder: 'Say something to {n}…', answer: 'Answer {n}', talking: 'Talking with {n}', said: 'YOU · SAID', you: 'YOU', photo: 'YOU · PHOTO' };

/** Choices under her line (D_Desk `choices`). The phone's chips for the same modes keep their words. */
export const CHOICE = {
  myDay: 'Here’s my day…', photo: 'Photo of my list', looksGood: 'Looks good', change: 'Change something', neverMind: 'Never mind',
  somethingElse: 'Something else', goodnight: 'Good night', shortDay: 'Plan a short day', offerYes: 'Sure, 2 minutes', offerNo: 'Not now',
  accept: 'Accept', vip: 'See VIP',
};

/** One-line notes in the subtitles (what changed). */
export const NOTE = {
  looksGood: 'Looks good.',
  moved: 'Moved “{t}” to tomorrow', retimed: 'Moved “{t}” to {x}', split: 'Split “{t}” into {k} small cards', removed: 'Removed “{t}”',
  notInPlan: 'Not in the plan: removed “{t}”', added: 'Added “{t}” · {x}', undo: 'Undo', keepOne: 'Keep it as one', undone: 'Undone',
  first: 'Card 1 · done: {t}', tomorrow: 'First on tomorrow’s list · {x} · {t}', tonight: 'Tonight · {x} · {t}',
  summary: '{d} done', summaryMoved: ' · {k} moved to tomorrow', planAt: 'Tomorrow we plan at {x}', changeTime: 'Change',
  week: 'Your week · {x}', seeWeek: 'See your week', nudge: '{t} · {k} min', inToday: 'In Today', expired: 'Expired',
  read: 'Read it', see: 'See it', notThrough: 'Didn’t go through.', tryAgain: 'Try again', working: 'Turning it into text…',
};

export const TOAST = {
  done: '{t}: done · +{k} points', later: '{t}: moved to {x}', removed: 'Removed: {t}', onDesk: '{k} cards on your desk',
  onDeskOne: '1 card on your desk', changeHint: 'Click a card to fix it, or just tell {n}', switched: 'Now with {n}. Your cards and streak stay the same.',
  cancelled: 'Cancelled. Nothing was sent.', tooShort: 'Too short. Nothing was sent.', locked: 'Opens at level {k}',
};

/** Us / Me journal (D_Desk `jr`). */
export const JOURNAL = {
  us: 'Us', me: 'Me', usAria: 'Us: you and {n}', usSub: 'You and {n} · day {k} together', usSubNew: 'You and {n} · you met today',
  streak: '{k}-day streak', lit: 'Today is lit.', notLit: 'One card today keeps it going.',
  level: 'Level {k}', points: '{a} / {b} points', next: 'NEXT · LEVEL {k}', away: '{k} points to go, about {c}.', allOpen: 'Everything on the route is open.',
  week: 'THIS WEEK', weekLine: '{c} · {l} lit', share: 'Share your week', stage: 'STAGE {k} OF 7', story: 'STORY', keeps: 'KEEPSAKES · {k} OF {t}',
  read: 'Read', fresh: 'New', meSub: '{e} · Day {k} · Invite {i}', buddy: 'YOUR BUDDY', buddyLine: '{r}, {p}', switch: 'SWITCH',
  remembers: 'WHAT {N} REMEMBERS', rememberNone: '{n} hasn’t saved anything yet.', free: 'Free', freeSub: '{k} messages a day', vip: 'VIP (simulated)',
  vipSub: 'Talk as much as you want', tryVip: 'Try VIP', manage: 'Manage', day: 'YOUR DAY', planAt: 'We plan at', checkins: 'Check-ins',
  tz: 'Time zone', tzAuto: 'Auto · {c}', computer: 'ON THIS COMPUTER', help: 'Need help now?', signOut: 'Sign out', tools: 'Test tools',
  close: 'Close the journal',
};

/** "On this computer" (the shell's switches, D2): D_Desk PREFS. `wander` is phase 2 and stays out of this release. */
export const PREFS: { key: 'stay' | 'notice' | 'meetings'; label: string; sub: (pron: 'she' | 'he') => string }[] = [
  { key: 'stay', label: 'Stay on screen when I switch apps', sub: (p) => `Whenever Xiaoman is open. The – button tucks ${p === 'he' ? 'him' : 'her'} into the menu bar.` },
  { key: 'notice', label: 'Notice when I stop typing', sub: (p) => `${p === 'he' ? 'He' : 'She'} looks up after a few quiet minutes. No words.` },
  { key: 'meetings', label: 'Step aside for calls', sub: () => 'Hides when a call app is in front, back when it ends.' },
];

export const SOUND = {
  head: 'SOUND', unlocked: 'Unlocked at level {k}', locks: 'Unlocks at level {k}', soon: 'Coming soon',
  foot: 'The light follows your clock: {p} place turns from day to night on its own.',
};

/** Glance (she looks up) / nod (she nods): the clip lengths (5 s, Arjun 6 s) minus the fade. D_Desk rEnd. */
export const REACT_MS = (ch: CharId) => (ch === 'arjun' ? 5800 : 4850);
/** Mouse still this long: the top UI fades and only the last two lines stay (D_Desk `quiet`). */
export const QUIET_MS = 4500;
/** The in-room title goes away by itself (D_Desk beat()). */
export const TITLE_MS = 7000;
/** Stamp + toss on the desk card before the next card shows (D_Desk `st`). */
export const STAMP_MS = 2100;
